import { Draft } from '@reduxjs/toolkit'
import { OrderSummaryOrderItem, TicketItemStatus } from '@ancon/wildcat-types'

import { CheckOrderTicketEntityTicketItem, OrderReducerState } from '../types'

/**
 * This function updates multiple ticket items statuses. Ticket items can be belong to different tickets.
 *
 * @param ticketItemsStatuses - A record mapping ticket item IDs to their new statuses.
 * @returns An array of updated ticket IDs.
 */
function updateTicketItemsStatuses(
  state: Draft<OrderReducerState>,
  ticketItemsStatuses: Record<string, TicketItemStatus>,
) {
  const updatedTicketIds: string[] = []

  // Order item IDs of the ticket items to be updated in STEP 2
  const orderItemIds: string[] = []

  const ticketIds = state.checkOrderSummaryTickets.ids

  const orderSummary = state.checkOrderSummary

  function updateTicketItemStatus<
    T extends {
      id: string
      status: TicketItemStatus
    },
  >(ticketItem: Draft<T>) {
    const { id, status } = ticketItem

    const ticketItemNewStatus = ticketItemsStatuses[id]

    if (id in ticketItemsStatuses && ticketItemNewStatus > status) {
      // eslint-disable-next-line no-param-reassign
      ticketItem.status = ticketItemNewStatus
      return true
    }

    return false
  }

  // STEP 1: Update ticket items in all ticket entities
  ticketIds.forEach(ticketId => {
    const ticketEntity = state.checkOrderSummaryTickets.entities[ticketId]

    if (ticketEntity) {
      let isTicketEntityUpdated = false

      ticketEntity.ticketItems.reduce<CheckOrderTicketEntityTicketItem[]>(
        (acc, ticketItem) => {
          const { orderItemId, ticketItems } = ticketItem

          const subProductsTicketItems = ticketItems ?? []
          const isMultiProductTicketItem = subProductsTicketItems.length > 0

          let isTicketItemUpdated = false

          // Update parent ticket item status
          if (updateTicketItemStatus(ticketItem)) {
            isTicketItemUpdated = true
            isTicketEntityUpdated = true
          }

          if (isMultiProductTicketItem) {
            // Update sub products ticket items
            subProductsTicketItems.forEach(subProductTicketItem => {
              if (updateTicketItemStatus(subProductTicketItem)) {
                isTicketItemUpdated = true
                isTicketEntityUpdated = true
              }
            })
          }

          // If ticket item updated, store order it's order item ID to update later in STEP 2
          if (isTicketItemUpdated && !orderItemIds.includes(orderItemId)) {
            orderItemIds.push(orderItemId)
          }

          acc.push(ticketItem)

          return acc
        },
        [],
      )

      if (isTicketEntityUpdated) {
        updatedTicketIds.push(ticketId as string)
      }
    }
  })

  // STEP 2: Update ticket items in order items of summary
  if (orderSummary) {
    orderSummary.items = orderSummary.items.reduce<OrderSummaryOrderItem[]>(
      (acc, orderItem) => {
        const { id, items } = orderItem

        // If this order item has ticket items to be updated
        if (orderItemIds.includes(id)) {
          const subProducts = items ?? []
          const isMultiProductOrderItem = subProducts.length > 0

          // Update ticket items status of the order items
          orderItem.ticketItems.forEach(updateTicketItemStatus)

          if (isMultiProductOrderItem) {
            // Update sub products ticket items
            subProducts.forEach(subProduct => {
              subProduct.ticketItems.forEach(updateTicketItemStatus)
            })
          }
        }

        acc.push(orderItem)

        return acc
      },
      [],
    )
  }

  return updatedTicketIds
}

export default updateTicketItemsStatuses
